import React from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { useRoleBasedNavigation } from '@/hooks/useRoleBasedNavigation';
import { Navigate, Link } from 'react-router-dom';
import { Users, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isDemoMode } = useDemo();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { getSuggestedRoute, isPractitioner } = useRoleBasedNavigation();

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Require authentication first
  if (!user && !isDemoMode) {
    return <Navigate to="/auth" replace />;
  }

  // Require admin role
  if (!isAdmin && !isDemoMode) {
    const suggestedRoute = getSuggestedRoute('/admin');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-100 dark:from-red-950/30 dark:via-orange-950/30 dark:to-yellow-950/30 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this area. Admin privileges are required.
          </p>
          
          {/* Show suggested route if user has practitioner access */}
          {suggestedRoute && isPractitioner && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                You have practitioner access. Try accessing your dashboard instead:
              </p>
              <Link to={suggestedRoute.route}>
                <Button className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Go to {suggestedRoute.label}</span>
                </Button>
              </Link>
            </div>
          )}
          
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Go Back</span>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;